import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {OcGenericDialog, OcGenericDialogAction, OcGenericDialogTitleProps} from '@oc/components'
import {useViewLoader} from '@oc/shell-core'
import {User} from '@oc/user-management'
import {useOcAppBarState} from '../../../Contexts/OcAppBarStateContext'
import {EditProfileDialogActions} from './editProfileDialogInterfaces'
import {messages} from '../../translations'
import {getEditProfileDialogDataGroups} from './editProfileDialogDatagroups'

const useStyles = makeOcStyles(theme => ({
    boldText: {
        fontWeight: theme.typography.fontWeightBold?.valueOf() as number
    },
    oneLineWrapper: {
        display: 'flex',
        whiteSpace: 'break-spaces'
    }
}))

function getDialogActions(): {[key: string]: OcGenericDialogAction} {
    const actions: {[key: string]: OcGenericDialogAction} = {}
    actions[EditProfileDialogActions.Cancel] = {
        label: messages.getText('appBar', 'cancel'),
        closeWithErrors: true,
        buttonBaseProps: {
            color: 'secondary',
            'data-cy': 'app-bar-edit-profile-dialog-cancel-button'
        }
    }

    actions[EditProfileDialogActions.Update] = {
        label: messages.getText('appBar', 'save'),
        closeWithErrors: false,
        applyChanges: true,
        validation: true,
        disableWithoutChange: true,
        buttonBaseProps: {
            'data-cy': 'app-bar-edit-profile-dialog-confirm-button'
        }
    }

    return actions
}

function getDialogTitleProps(
    username: string,
    boldTextClassName: string,
    oneLineWrapperClassName: string
): OcGenericDialogTitleProps {
    const secondRowLabel = `${messages.getText('appBar', 'edit')} ${messages.getText('appBar', 'userInformation')} `

    const titleProps: OcGenericDialogTitleProps = {
        firstRow: {
            left: <Typography className={boldTextClassName}>{messages.getText('appBar', 'userDetails')}</Typography>
        },
        secondRow: {
            left: (
                <div className={oneLineWrapperClassName}>
                    <Typography>{secondRowLabel}</Typography>
                    <Typography className={boldTextClassName}>{username}</Typography>
                </div>
            )
        }
    }
    return titleProps
}

function _EditProfileDialog() {
    const state = useOcAppBarState()
    const {classes} = useStyles()
    const loader = useViewLoader()
    const data = state.loggedUser ? [state.loggedUser] : []

    const onCloseCallback = React.useCallback(
        async (actionKey: string) => {
            const onClose = async () => {
                switch (actionKey as EditProfileDialogActions) {
                    case EditProfileDialogActions.Update:
                        await state.saveUserInfo()
                        state.toggleEditProfileDialog(false)
                        break
                    case EditProfileDialogActions.Cancel:
                        state.toggleEditProfileDialog(false)
                }
            }
            loader.whileLoading(onClose())
        },
        [state, loader]
    )

    return (
        <OcGenericDialog<User>
            open={state.isEditProfileDialogOpen}
            data={data}
            onClose={onCloseCallback}
            defaultDialogTitleProps={getDialogTitleProps(
                state.loggedUser?.username || '',
                classes.boldText,
                classes.oneLineWrapper
            )}
            dataGroups={getEditProfileDialogDataGroups(state)}
            actions={getDialogActions()}
            data-cy="app-bar-edit-user-profile-dialog"
        />
    )
}

export const EditProfileDialog = observer(_EditProfileDialog)
